import React from 'react'
import { useTranslation } from 'react-i18next'
import { useQuery } from '@apollo/react-hooks'
import { Pin } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '@/components/ui/sheet'
import { costChangeStatusVariant, costContractStatusVariant, costInvoiceStatusVariant } from '@/lib/status-variants'
import { cn } from '@/lib/utils'
import * as Query from '../../../api/gql/query'
import { BASE_COLUMN, COLUMN_LABEL_KEY, FORMULA, drillKind, type ValueKey } from '../lib/columns'
import { describeCostControlError } from '../lib/errors'
import { CHANGE_STATUS_KEY, CONTRACT_STATUS_KEY, INVOICE_STATUS_KEY } from '../lib/labels'
import { formatMoney, isNegativeMoney } from '../lib/money'
import type { CostTree } from '../lib/tree'
import type { CostBreakdownEntry, CostChangeStatus, CostContractStatus, CostInvoiceStatus, CostRow, CostSourceType } from '../types'
import BreakdownSheetSkeleton from './BreakdownSheetSkeleton'

export interface BreakdownTarget { nodeId: string; key: ValueKey }

const SOURCE_LABEL_KEY: Record<CostSourceType, string> = {
  BUDGET: 'CostControl_source_BUDGET',
  CONTRACT: 'CostControl_source_CONTRACT',
  CHANGE: 'CostControl_source_CHANGE',
  INVOICE: 'CostControl_source_INVOICE',
  EXPENSE: 'CostControl_source_EXPENSE',
}

/** The inputs of the forecast rule (spec §1.3) shown under it. */
const FORECAST_PARTS: ValueKey[] = ['revisedBudget', 'committed', 'pendingContractChanges', 'potentialChanges', 'actual']

interface Props {
  projectId: string
  target: BreakdownTarget | null
  tree: CostTree
  currency: string
  locale: string
  onNavigate: (key: ValueKey) => void
  onClose: () => void
  /** Opens a record entry (spec §10: every total opens to the records behind it). */
  onOpenRecord?: (sourceType: CostSourceType, id: string) => void
}

const BreakdownSheet: React.FC<Props> = ({ projectId, target, tree, currency, locale, onNavigate, onClose, onOpenRecord }) => {
  const { t } = useTranslation()
  const row = target ? tree.byId.get(target.nodeId)?.activity : undefined
  const column = target ? BASE_COLUMN[target.key] : undefined
  const { data, loading, error } = useQuery<{ costBreakdown: CostBreakdownEntry[] }>(Query.COST_BREAKDOWN, {
    variables: { projectId, nodeId: target?.nodeId, column },
    skip: !target || !column,
    fetchPolicy: 'network-only',
    context: { handlesErrors: true },
  })
  const money = (v: string) => formatMoney(v, { locale, currency })
  const entries = column ? data?.costBreakdown : undefined

  const detail = (e: CostBreakdownEntry) =>
    [
      t(SOURCE_LABEL_KEY[e.sourceType]),
      e.counterparty,
      e.nodeId === null ? t('CostControl_breakdown_nodes', { count: e.nodeCount }) : e.nodeName !== e.label ? e.nodeName : null,
    ].filter(Boolean).join(' · ')

  const statusBadge = (e: CostBreakdownEntry) => {
    // Every entry behind Potential changes is a draft; "Koncept" would mislabel an open potential change (decision 15).
    if (!e.status || column === 'POTENTIAL_CHANGES') return null
    if (e.sourceType === 'CONTRACT' && e.status in CONTRACT_STATUS_KEY) return <Badge variant={costContractStatusVariant(e.status)} className="shrink-0">{t(CONTRACT_STATUS_KEY[e.status as CostContractStatus])}</Badge>
    if (e.sourceType === 'CHANGE' && e.status in CHANGE_STATUS_KEY) return <Badge variant={costChangeStatusVariant(e.status)} className="shrink-0">{t(CHANGE_STATUS_KEY[e.status as CostChangeStatus])}</Badge>
    if (e.sourceType === 'INVOICE' && e.status in INVOICE_STATUS_KEY) return <Badge variant={costInvoiceStatusVariant(e.status)} className="shrink-0">{t(INVOICE_STATUS_KEY[e.status as CostInvoiceStatus])}</Badge>
    return null
  }

  const part = (r: CostRow, key: ValueKey, sign: 1 | -1 | null) => (
    <li key={key}>
      <button type="button" className="flex w-full items-center gap-3 px-3 py-2 text-left text-sm hover:bg-accent" onClick={() => onNavigate(key)}>
        {sign !== null && <span className="w-3 text-muted-foreground">{sign === 1 ? '+' : '−'}</span>}
        <span className="min-w-0 flex-1 truncate underline-offset-2 hover:underline">{t(COLUMN_LABEL_KEY[key])}</span>
        <span className="shrink-0 tabular-nums">{money(r.values[key])}</span>
      </button>
    </li>
  )

  return (
    <Sheet open={!!target && !!row} onOpenChange={(open) => { if (!open) onClose() }}>
      <SheetContent side="right" className="w-full overflow-y-auto sm:max-w-xl">
        {target && row && (
          <>
            <SheetHeader>
              <SheetTitle>{t(COLUMN_LABEL_KEY[target.key])}</SheetTitle>
              <SheetDescription>{row.code ? `${row.code} · ${row.name}` : row.name}</SheetDescription>
            </SheetHeader>
            <div className="mt-4 space-y-3">
              {drillKind(target.key) === 'records' && (
                <>
                  {loading && <BreakdownSheetSkeleton />}
                  {error && <p className="text-sm text-destructive">{describeCostControlError(error.message, t)}</p>}
                  {entries && entries.length === 0 && <p className="text-sm text-muted-foreground">{t('CostControl_breakdown_empty')}</p>}
                  {entries && entries.length > 0 && (
                    <ul className="divide-y rounded-md border">
                      {entries.map((e) => (
                        <li key={`${e.sourceType}:${e.sourceId}`} className="flex items-start gap-3 px-3 py-2 text-sm">
                          <div className="min-w-0 flex-1">
                            {e.sourceType !== 'BUDGET' && onOpenRecord ? (
                              <button
                                type="button"
                                className="block max-w-full truncate text-left font-medium text-primary hover:underline"
                                onClick={() => onOpenRecord(e.sourceType, e.sourceId)}
                                title={t('CostControl_breakdown_open_record')}
                              >
                                {e.number ? `${e.number} · ${e.label}` : e.label}
                              </button>
                            ) : (
                              <div className="truncate font-medium">{e.number ? `${e.number} · ${e.label}` : e.label}</div>
                            )}
                            <div className="truncate text-xs text-muted-foreground">{detail(e)}</div>
                          </div>
                          {statusBadge(e)}
                          <span className="shrink-0 tabular-nums">{money(e.amount)}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </>
              )}
              {drillKind(target.key) === 'formula' && (
                <div className="space-y-2">
                  <p className="text-xs text-muted-foreground">{t('CostControl_formula_parts')}</p>
                  <ul className="divide-y rounded-md border">{(FORMULA[target.key] ?? []).map((term) => part(row, term.key, term.sign))}</ul>
                </div>
              )}
              {drillKind(target.key) === 'forecast' && (
                <div className="space-y-3">
                  <div className="space-y-1 text-sm">
                    <p className="font-medium">{t('CostControl_forecast_rule_title')}</p>
                    {row.kind === 'GROUP' ? (
                      <p className="text-muted-foreground">{t('CostControl_forecast_rule_group')}</p>
                    ) : (
                      <ol className="list-decimal space-y-1 pl-5 text-muted-foreground">
                        <li>{t('CostControl_forecast_rule_override')}</li>
                        <li>{t(row.costBasedForecast || row.hasForecastOverride || row.estimate === null ? 'CostControl_forecast_rule_untendered' : 'CostControl_breakdown_forecast_estimate')}</li>
                        <li>{t('CostControl_forecast_rule_committed')}</li>
                      </ol>
                    )}
                    {row.hasForecastOverride && (
                      <p className="flex items-center gap-1"><Pin className="h-3.5 w-3.5" />{t('CostControl_forecast_override_active')}</p>
                    )}
                  </div>
                  {row.kind === 'ITEM' && (
                    <ul className="divide-y rounded-md border">{FORECAST_PARTS.map((key) => part(row, key, null))}</ul>
                  )}
                </div>
              )}
            </div>
            <div className="mt-4 flex items-center justify-between border-t pt-3 font-semibold">
              <span>{t('CostControl_breakdown_total')}</span>
              <span className={cn('tabular-nums', isNegativeMoney(row.values[target.key]) && 'text-destructive')}>{money(row.values[target.key])}</span>
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  )
}
export default BreakdownSheet
